import '#library/temporal.polyfill.js';
import { fromRoman } from '#library/number.library.js';
import { asType } from '#library/type.library.js';
import { LOG } from '#library/logger.class.js';
import { isNull, isString, isObject, isPlainObject, isZonedDateTime, isInstant, isDefined, isUndefined, isEmpty, isNumber, isDigit, isNumeric, isFunction, isText } from '#library/assertion.library.js';
import { WeakCache } from '#library/weakcache.class.js';
import { evaluate } from '#library/evaluation.library.js';
import { asArray } from '#library/coercion.library.js';
import { instant, getTemporalIds } from '#library/temporal.library.js';
import { ownKeys, ownEntries, hasOwn } from '#library/primitive.library.js';
import { Enum } from '#library/enumerate.library.js';
import { resolveTermValue } from '../engine/engine.term.js';
import { selectLayoutPatterns } from '../engine/engine.planner.js';
import { compose } from '../engine/engine.composer.js';
import { normalizeMatch, accumulateResult } from '../engine/engine.normalizer.js';
import { getRange, getTermRange, findTermPlugin } from '../plugin/term/term.util.js';
import { defineInterpreterModule } from '../plugin/plugin.util.js';
import { sym, isTempo, TermError, TermHook, getRuntime, Match, TempoError, $setEvents, $setPeriods, markConfig, setPatterns, init, extendState, enums, Token, Snippet, hasNativeFormatBraces } from '#tempo/support';
import { setProperty, logError, logDebug } from '#tempo/support/support.util.js';
/**
 * Builds a parse-cache key from normalized input and parsing configuration.
 *
 * @param str - The input text to include in the key
 * @param today - The anchor date used for date-sensitive parsing
 * @param state - The parser state whose temporal configuration scopes the key
 * @returns A cache key containing the input, anchor date, time zone, calendar, locale, and hemisphere
 */
function buildCacheKey(str, today, state, formatOpt, dialectOpt) {
    const norm = str.trim().toLowerCase();
    const dateSalt = today.toPlainDate().toString();
    const tz = String(state.config.timeZone || 'UTC');
    const cal = String(state.config.calendar || 'iso8601');
    const loc = Array.isArray(state.config.locale) ? state.config.locale.join(',') : String(state.config.locale || 'en-US');
    const sph = String(state.config.sphere || 'north');
    const fmtKey = formatOpt ? asArray(formatOpt).map(String).join('|') : '';
    const dialect = String(dialectOpt ?? '');
    return `${norm}::${dateSalt}::${tz}::${cal}::${loc}::${sph}::${fmtKey}::${dialect}`;
}
const BRACED_CACHE = new WeakCache();
const RE_SINGLE_WHITESPACE = /\s/;
const RE_REGEX_ESCAPE_CHAR = /[\\^$*+?.()|[\]{}]/;
const RE_SIGNED_LEADING_DIGITS = /^-?\d+/;
const RE_ORDINAL_SUFFIX = /(?:st|nd|rd|th)/;
const RE_TERM_ID = /#[\w.]+/;
const RE_ROMAN_DIGITS = '[IVXLCDMivxlcdm]+';
const RE_ROMAN_CHARS = /^[IVXLCDMivxlcdm]+$/;
/** Parses a capture group as Roman numerals if it matches Roman characters, otherwise parses as decimal */
function parseRomanOrDecimal(val) {
    const trimmed = val.trim();
    if (RE_ROMAN_CHARS.test(trimmed)) {
        const r = fromRoman(trimmed);
        if (Number.isFinite(r))
            return r;
    }
    return parseInt(trimmed, 10);
}
/** Constructs a regular expression to match ordinal term expressions (e.g. "3rd day of #qtr.2"). */
function buildOrdinalTermPattern(ordWords) {
    const ordPattern = isText(ordWords)
        ? `-?\\d+${RE_ORDINAL_SUFFIX.source}?|${ordWords.trim()}`
        : `-?\\d+${RE_ORDINAL_SUFFIX.source}?`;
    return new RegExp(`^\\s*(?<ord>${ordPattern})\\s+(?:day\\s+of\\s+)?(?<term>${RE_TERM_ID.source})\\s*$`, 'i');
}
/**
 * Compiles a native braced format mask into a cached parsing expression.
 * The roman modifier accepts numeral text for year, month, day, and 24-hour tokens.
 * @throws Error if the mask contains an unknown braced token.
 */
function compileBracedPattern(fmt) {
    let cached = BRACED_CACHE.get(fmt);
    if (cached)
        return cached;
    const groupCounts = {};
    const group = (name, regex) => {
        const count = (groupCounts[name] = (groupCounts[name] ?? 0) + 1);
        const groupName = count === 1 ? name : `${name}_${count}`;
        return `(?<${groupName}>${regex})`;
    };
    let pattern = '';
    let i = 0;
    while (i < fmt.length) {
        if (fmt[i] === '{') {
            const end = fmt.indexOf('}', i);
            if (end === -1) {
                pattern += '\\{';
                i++;
                continue;
            }
            const rawToken = fmt.slice(i + 1, end).trim();
            const [tok, ...modifiers] = rawToken.split(':');
            const token = tok.toLowerCase();
            const isRoman = modifiers.some(m => m.toLowerCase() === 'roman');
            const romanOr = (fallback) => isRoman ? RE_ROMAN_DIGITS : fallback;
            switch (token) {
                case 'yyyy':
                case 'year':
                    pattern += group('yyyy', romanOr('[+-]?\\d{4,6}|\\d{4}'));
                    break;
                case 'yy':
                    pattern += group('yy', romanOr('\\d{2}'));
                    break;
                case 'mm':
                case 'month':
                    pattern += group('mm', romanOr('\\d{1,2}'));
                    break;
                case 'mmm':
                    pattern += group('mmm', '[A-Za-z]{3,}');
                    break;
                case 'mon':
                    pattern += group('mon', '[A-Za-z]+');
                    break;
                case 'dd':
                case 'day':
                    pattern += group('dd', romanOr('\\d{1,2}(?:st|nd|rd|th)?'));
                    break;
                case 'hh':
                case 'h24':
                case 'hour':
                    pattern += group('hh', romanOr('\\d{1,2}'));
                    break;
                case 'h12':
                    pattern += group('h12', '1[0-2]|0?[1-9]');
                    break;
                case 'mi':
                case 'min':
                case 'minute':
                    pattern += group('mi', '\\d{1,2}');
                    break;
                case 'ss':
                case 'sec':
                case 'second':
                    pattern += group('ss', '\\d{1,2}');
                    break;
                case 'ms':
                case 'millisecond':
                    pattern += group('ms', '\\d{1,3}');
                    break;
                case 'mer':
                case 'ampm':
                    pattern += group('mer', '[AaPp][Mm]?');
                    break;
                case '*':
                case '_':
                case 'skip':
                    pattern += '(?:.*?)';
                    break;
                default:
                    throw new Error(`[Tempo] Unknown braced format token '{${rawToken}}' in format mask '${fmt}'.`);
            }
            i = end + 1;
        }
        else {
            const ch = fmt[i];
            if (RE_SINGLE_WHITESPACE.test(ch)) {
                pattern += '\\s+';
                while (i + 1 < fmt.length && RE_SINGLE_WHITESPACE.test(fmt[i + 1]))
                    i++;
            }
            else if (RE_REGEX_ESCAPE_CHAR.test(ch)) {
                pattern += '\\' + ch;
            }
            else {
                pattern += ch;
            }
            i++;
        }
    }
    const reg = new RegExp(`^${pattern}$`, 'i');
    BRACED_CACHE.set(fmt, reg);
    return reg;
}
/** Resolves a localized full or abbreviated English month name to its month number. */
function resolveMonthNum(str) {
    const cap = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    return Enum.get(enums.MONTH, cap) ?? Enum.get(enums.MONTHS, cap) ?? Enum.get(enums.MONTH, cap.slice(0, 3));
}
/**
 * Parses input with a native braced mask, using the supplied date and calendar defaults.
 * Roman year, month, day, and 24-hour captures are converted to numbers; short years use a cutoff of 70.
 * Returns undefined for a mismatch or a caught Temporal date, time zone, or calendar construction error.
 * @throws Error for unknown braced tokens; mask compilation errors propagate to the caller.
 */
function parseBracedFormat(input, fmt, today, tz, cal) {
    const rx = compileBracedPattern(fmt);
    const match = rx.exec(input);
    if (!match || !match.groups)
        return undefined;
    const g = match.groups;
    const getGroup = (name) => g[name] ?? Object.entries(g).find(([k]) => k === name || k.startsWith(`${name}_`))?.[1];
    let year = today.year;
    const yyyy = getGroup('yyyy');
    const yyVal = getGroup('yy');
    if (yyyy) {
        year = parseRomanOrDecimal(yyyy);
    }
    else if (yyVal) {
        const yy = parseRomanOrDecimal(yyVal);
        year = yy < 100 ? (yy >= 70 ? 1900 + yy : 2000 + yy) : yy;
    }
    let month = 1;
    const mm = getGroup('mm');
    const mmm = getGroup('mmm');
    const mon = getGroup('mon');
    if (mm) {
        month = parseRomanOrDecimal(mm);
    }
    else if (mmm || mon) {
        const mNum = resolveMonthNum(mmm || mon);
        if (mNum)
            month = mNum;
        else
            return undefined;
    }
    else if (!yyyy && !yyVal) {
        month = today.month;
    }
    let day = 1;
    const dd = getGroup('dd');
    if (dd) {
        day = parseRomanOrDecimal(dd);
    }
    else if (!yyyy && !yyVal && !mm && !mmm && !mon) {
        day = today.day;
    }
    let hour = 0;
    let minute = 0;
    let second = 0;
    let millisecond = 0;
    const hh = getGroup('hh');
    const h12 = getGroup('h12');
    if (hh) {
        hour = parseRomanOrDecimal(hh);
    }
    else if (h12)
        hour = parseInt(h12, 10);
    const mer = getGroup('mer');
    if (mer) {
        const isPm = mer.toLowerCase().startsWith('p');
        if (isPm && hour < 12)
            hour += 12;
        else if (!isPm && hour === 12)
            hour = 0;
    }
    const mi = getGroup('mi');
    if (mi)
        minute = parseInt(mi, 10);
    const ss = getGroup('ss');
    if (ss)
        second = parseInt(ss, 10);
    const ms = getGroup('ms');
    if (ms)
        millisecond = parseInt(ms.padEnd(3, '0').slice(0, 3), 10);
    try {
        return Temporal.PlainDateTime.from({
            year,
            month,
            day,
            hour,
            minute,
            second,
            millisecond
        }, { overflow: 'reject' }).toZonedDateTime(tz).withCalendar(cal);
    }
    catch (e) {
        return undefined;
    }
}
/** Delegates parsing of an external format mask to the registered dialect plugin. */
function parseDialectFormat(input, fmt, state, today, tz, cal, dialectOpt) {
    const dialect = dialectOpt ?? evaluate(state.options?.dialect ?? state.config?.dialect);
    const TempoClass = getRuntime().modules['Tempo'];
    const dialectsRegistry = state.config?.registry?.dialects
        ?? getRuntime().dialects
        ?? getRuntime().modules?.['DialectsModule']
        ?? globalThis[Symbol.for('Tempo.dialects')]
        ?? TempoClass?.dialectsRegistry;
    const parser = dialectsRegistry?.parse ?? (dialect ? dialectsRegistry?.[dialect]?.parse : undefined);
    if (isFunction(parser)) {
        return parser(input, fmt, {
            ...state.config,
            ...state.options,
            pivot: state.options?.pivot ?? state.parse?.pivot ?? state.config?.pivot,
            timeZone: tz,
            calendar: cal,
            dialect,
            today,
        });
    }
    throw new Error(`[Tempo] Parsing unbraced format mask '${fmt}' requires '@magmacomputing/tempo-plugin-dialects'.`);
}
/**
 * Internal Parse Engine Implementation
 */
const _ParseEngine = {
    /** parse DateTime input */
    parse(state, tempo, dateTime, term) {
        if (isNull(tempo)) {
            state.errored = true;
            return undefined;
        }
        if (!term && (isZonedDateTime(tempo) || isInstant(tempo))) {
            const { config } = state;
            const [tz, cal] = getTemporalIds(config.timeZone, config.calendar);
            const dt = isZonedDateTime(tempo) ? tempo : tempo.toZonedDateTimeISO(tz);
            return {
                type: 'Temporal.ZonedDateTime',
                value: dt.withTimeZone(tz).withCalendar(cal),
                zone: tz,
                calendar: cal
            };
        }
        state.parseDepth = (state.parseDepth ?? 0) + 1;
        const isRoot = state.parseDepth === 1;
        if (isRoot) {
            if (!Array.isArray(state.parse.result))
                setProperty(state.parse, 'result', []);
            state.parse.result.length = 0;
        }
        let today;
        try {
            const { config } = state;
            const TempoClass = getRuntime().modules['Tempo'];
            const terms = state.pluginsDb.terms;
            const val = dateTime ?? state.anchor ?? state.config.anchor ?? (isTempo(tempo) ? tempo.toDateTime() : (isZonedDateTime(tempo) ? tempo : (isInstant(tempo) ? tempo.toZonedDateTimeISO(config.timeZone) : undefined)));
            const [tz, cal] = getTemporalIds(config.timeZone, config.calendar);
            let basis;
            if (isTempo(val))
                basis = val.toDateTime();
            else if (isZonedDateTime(val))
                basis = val;
            else if (isDefined(val)) {
                const safeConfig = { ...state.config };
                delete safeConfig.anchor;
                if (TempoClass) {
                    basis = TempoClass.from(val, safeConfig).toDateTime();
                }
                else {
                    const ms = val instanceof Date ? val.getTime() : (isDigit(val) ? Number(val) : new Date(String(val)).getTime());
                    basis = Temporal.Instant.fromEpochMilliseconds(ms || Date.now()).toZonedDateTimeISO(tz).withCalendar(cal);
                }
            }
            else {
                basis = instant().toZonedDateTimeISO(tz).withCalendar(cal);
            }
            const isAnchored = isDefined(val);
            if (isRoot) {
                state.parse.anchor = basis;
                state.parse.isAnchored = isAnchored;
            }
            today = basis;
            if (term) {
                const ident = term.startsWith('#') ? term.slice(1) : term;
                const termObj = terms.find((termEntry) => termEntry.key === ident || termEntry.scope === ident);
                if (!termObj) {
                    if (TempoClass)
                        TempoClass[TermError](state.config, term);
                    return undefined;
                }
                if (isNumeric(tempo)) {
                    const list = getRange(termObj, state, today);
                    const current = getTermRange(state, list, false, today);
                    if (!current)
                        throw new RangeError(`Term index out of range: ${tempo} for ${term}`);
                    const isMultiCycle = isDefined(termObj.resolve) && list.some(r => r.year !== undefined);
                    const itemsPerCycle = isMultiCycle ? list.length / 3 : list.length;
                    const currentIdx = list.findIndex(r => r.key === current.key && (isMultiCycle ? r.year === current.year : true));
                    if (currentIdx === -1 || itemsPerCycle <= 0)
                        throw new RangeError(`Term index out of range: ${tempo} for ${term}`);
                    const cycleOffset = Math.floor(currentIdx / itemsPerCycle) * itemsPerCycle;
                    const targetIdx = cycleOffset + (Number(tempo) - 1);
                    const item = list[targetIdx];
                    if (item) {
                        const range = getTermRange(state, [item], false, today);
                        if (range?.start)
                            return { type: 'Temporal.ZonedDateTime', value: range.start.toDateTime().withTimeZone(tz).withCalendar(cal), zone: tz, calendar: cal };
                    }
                    throw new RangeError(`Term index out of range: ${tempo} for ${term}`);
                }
                if (tempo === term) {
                    const range = termObj.define.call(state, false, today);
                    const list = isUndefined(range) ? [] : asArray(range);
                    const current = getTermRange(state, list, false, today);
                    if (current?.start)
                        return { type: 'Temporal.ZonedDateTime', value: current.start.toDateTime().withTimeZone(tz).withCalendar(cal), zone: tz, calendar: cal };
                }
            }
            if (isObject(tempo)) {
                const termKey = Object.keys(tempo).find(k => k.startsWith('#'));
                if (termKey) {
                    if (isUndefined(term)) {
                        const msg = `Unsupported Syntax: Term-based mutations (#) cannot be passed to the constructor. Use new Tempo().set(${JSON.stringify(tempo)}) instead.`;
                        logError(msg, state.config);
                        throw new TempoError(msg);
                    }
                    if (terms.length === 0) {
                        if (TempoClass)
                            TempoClass[TermError](state.config, termKey);
                        return undefined;
                    }
                }
            }
            const resolvingKeys = new Set();
            const res = _ParseEngine.conform(state, tempo, today, isAnchored, resolvingKeys);
            const { timeZone: tz2, calendar: cal2 } = state.config;
            const [targetTz, targetCal] = getTemporalIds(tz2, cal2);
            const { dateTime: dt, timeZone } = compose(res, today, tz, targetTz, targetCal, (m) => accumulateResult(state, m), state.config.timeStamp, state.config, state.userProvidedKeys);
            dateTime = dt;
            const hasExplicitTzOption = hasOwn(state.options, 'timeZone');
            const effectiveTz = hasExplicitTzOption ? targetTz : (timeZone ?? targetTz);
            if (isZonedDateTime(dateTime) && !state.errored) {
                dateTime = dateTime.withTimeZone(effectiveTz).withCalendar(targetCal);
                if (!hasExplicitTzOption && timeZone && state.config)
                    state.config.timeZone = effectiveTz;
            }
            if ((state.config.cache === true || state.config.cache === enums.CACHE.On || state.config.cache === enums.CACHE.Refresh || state.config.cache === 'refresh') && isString(tempo) && isZonedDateTime(dateTime) && !state.errored) {
                const formatOpt = evaluate(state.options?.format ?? state.config?.format);
                const dialectOpt = evaluate(state.options?.dialect ?? state.config?.dialect);
                const cacheKey = buildCacheKey(tempo, today, state, formatOpt, dialectOpt);
                state.cache.set(cacheKey, dateTime.toString());
            }
            return Object.assign(res, {
                type: 'Temporal.ZonedDateTime',
                value: (isZonedDateTime(dateTime) && !state.errored) ? dateTime : undefined
            });
        }
        finally {
            state.parseDepth--;
        }
    },
    /** conform input to a Temporal.ZonedDateTime */
    conform(state, tempo, dateTime, isAnchored = false, resolvingKeys = new Set()) {
        const arg = asType(tempo);
        let { type, value } = arg;
        const TempoClass = getRuntime().modules['Tempo'];
        const terms = state.pluginsDb.terms;
        if (isTempo(dateTime))
            dateTime = dateTime.toDateTime();
        if (!isZonedDateTime(dateTime)) {
            logError(new TypeError(`Sacred Anchor corrupted: ${String(value)}`), state.config);
            return { type: 'Void', value: undefined };
        }
        let zdt = dateTime;
        if (_ParseEngine.isZonedDateTimeLike(state, tempo)) {
            const { timeZone, calendar, value: _, ...options } = tempo;
            const termKey = Object.keys(options).find(k => k.startsWith('#'));
            if (termKey && terms.length === 0) {
                if (TempoClass)
                    TempoClass[TermError](state.config, termKey);
                return { type: 'Void', value: undefined };
            }
            if (timeZone)
                zdt = zdt.withTimeZone(timeZone);
            if (calendar)
                zdt = zdt.withCalendar(calendar);
            if (!isEmpty(options))
                zdt = zdt.with(options);
            accumulateResult(state, { type: 'Temporal.ZonedDateTimeLike', value: zdt, match: 'Temporal.ZonedDateTimeLike' });
            return { type: 'Temporal.ZonedDateTime', value: zdt };
        }
        if (isTempo(value)) {
            const res = value.toDateTime();
            const [tz, cal] = getTemporalIds(res);
            return { type: 'Temporal.ZonedDateTime', value: res, zone: tz, calendar: cal };
        }
        if (isZonedDateTime(value))
            return { type: 'Temporal.ZonedDateTime', value };
        if (isString(value) && value.includes('#')) {
            const hashIdx = value.indexOf('#');
            const termId = value.slice(hashIdx + 1).split(/[\s./]/)[0];
            const termObj = findTermPlugin(termId, state);
            if (termObj) {
                let memoAnchor;
                const getAnchor = () => {
                    if (memoAnchor !== undefined)
                        return memoAnchor;
                    try {
                        const optAnchor = state.options?.anchor;
                        memoAnchor = isTempo(optAnchor)
                            ? optAnchor
                            : (optAnchor && TempoClass ? new TempoClass(optAnchor, state.config) : (isTempo(dateTime) ? dateTime : ((TempoClass && isZonedDateTime(dateTime)) ? new TempoClass(dateTime, state.config) : undefined)));
                    }
                    catch {
                        memoAnchor = undefined;
                    }
                    return memoAnchor;
                };
                const ctx = {
                    get anchor() { return getAnchor(); },
                    timeZone: state.config?.timeZone,
                    calendar: state.config?.calendar,
                    locale: state.config?.locale,
                    geo: state.options?.geo ?? state.config?.geo,
                    config: state.config,
                };
                // 1. TermHook.parse: Explicit #term string expression
                if (value.startsWith('#') && isFunction(termObj[TermHook.parse])) {
                    const hooked = termObj[TermHook.parse](value, ctx);
                    if (hooked) {
                        const hookedZdt = isTempo(hooked) ? hooked.toDateTime() : (isZonedDateTime(hooked) ? hooked : undefined);
                        if (hookedZdt)
                            return { type: 'Temporal.ZonedDateTime', value: hookedZdt, zone: hookedZdt.timeZoneId };
                    }
                }
                // 2. TermHook.ordinal: e.g. "3rd day of #qtr.2"
                if (isFunction(termObj[TermHook.ordinal])) {
                    const rawLocalOrdinals = { ...state.config?.registry?.ordinals, ...state.options?.registry?.ordinals };
                    const localOrdinals = {};
                    for (const [k, v] of Object.entries(rawLocalOrdinals))
                        localOrdinals[k.toLowerCase()] = v;
                    const allOrdKeys = Array.from(new Set([
                        ...Enum.keys(enums.ORDINAL),
                        ...Object.keys(localOrdinals)
                    ])).filter(Boolean);
                    const ordWords = allOrdKeys.map(Match.escape).join('|');
                    const ordMatch = value.match(buildOrdinalTermPattern(ordWords));
                    if (ordMatch?.groups?.term) {
                        const [ordTermId] = ordMatch.groups.term.slice(1).split('.');
                        const targetTermObj = findTermPlugin(ordTermId, state);
                        if (targetTermObj === termObj) {
                            const anchorTempo = ctx.anchor ?? getAnchor();
                            const ordRaw = ordMatch.groups.ord?.toLowerCase();
                            const numericVal = RE_SIGNED_LEADING_DIGITS.test(ordRaw) ? parseInt(ordRaw, 10) : undefined;
                            const resolvedVal = localOrdinals[ordRaw] ?? (ordRaw ? enums.ORDINAL?.[ordRaw] : undefined) ?? numericVal;
                            const matchGroups = {
                                ...ordMatch.groups,
                                ...(isDefined(resolvedVal) ? { value: resolvedVal } : {})
                            };
                            const hooked = termObj[TermHook.ordinal](matchGroups, anchorTempo, ctx);
                            if (hooked) {
                                const hookedZdt = isTempo(hooked) ? hooked.toDateTime() : (isZonedDateTime(hooked) ? hooked : undefined);
                                if (hookedZdt)
                                    return { type: 'Temporal.ZonedDateTime', value: hookedZdt, zone: hookedZdt.timeZoneId };
                            }
                        }
                    }
                }
            }
            if (value.startsWith('#')) {
                const res = resolveTermValue(TempoClass, state, value, dateTime);
                if (isZonedDateTime(res))
                    return { type: 'Temporal.ZonedDateTime', value: res, zone: res.timeZoneId };
                return { type: 'Void', value: undefined };
            }
        }
        if (isString(value)) {
            let trim = value.trim();
            const normVal = trim.toLowerCase();
            const formatOpt = evaluate(state.options?.format ?? state.config?.format);
            const dialectOpt = evaluate(state.options?.dialect ?? state.config?.dialect);
            // 1. Static Glossary Check
            if (state.cache.isStatic(normVal)) {
                const staticTarget = state.cache.get(normVal);
                if (staticTarget) {
                    accumulateResult(state, { match: 'CacheHit', value: trim, source: 'glossary' });
                    return { type: 'String', value: staticTarget };
                }
            }
            // 2. Dynamic Parse Cache Check
            const cacheOpt = state.config.cache;
            if (cacheOpt === true || cacheOpt === enums.CACHE.On) {
                const cacheKey = buildCacheKey(trim, dateTime, state, formatOpt, dialectOpt);
                const cachedIso = state.cache.get(cacheKey);
                if (cachedIso) {
                    accumulateResult(state, { match: 'CacheHit', value: trim, source: 'parseCache' });
                    return { type: 'String', value: cachedIso };
                }
            }
            // 3. Explicit Format Mask Check (Native Braced or Dialect)
            if (isDefined(formatOpt)) {
                const formats = asArray(formatOpt);
                const [tz, cal] = getTemporalIds(state.config.timeZone, state.config.calendar);
                const dialect = dialectOpt ?? evaluate(state.options?.dialect ?? state.config?.dialect);
                for (const fmt of formats) {
                    if (!isString(fmt))
                        continue;
                    try {
                        const res = hasNativeFormatBraces(fmt, dialect)
                            ? parseBracedFormat(trim, fmt, dateTime, tz, cal)
                            : parseDialectFormat(trim, fmt, state, dateTime, tz, cal, dialectOpt);
                        if (isZonedDateTime(res)) {
                            accumulateResult(state, { match: 'FormatMask', value: trim, pattern: fmt });
                            return { type: 'Temporal.ZonedDateTime', value: res, zone: res.timeZoneId, calendar: cal };
                        }
                    }
                    catch (err) {
                        if (formats.length === 1)
                            throw err;
                    }
                }
                state.errored = true;
                return { type: 'Void', value: undefined };
            }
            if (state.parse.ignorePattern) {
                // Clone the RegExp: global/sticky flags maintain `lastIndex` state, which
                // cannot be mutated when `state.parse` is frozen (e.g. on a sandbox instance).
                const pat = Object.isFrozen(state.parse.ignorePattern)
                    ? new RegExp(state.parse.ignorePattern.source, state.parse.ignorePattern.flags)
                    : state.parse.ignorePattern;
                trim = trim.replace(pat, ' ').replace(Match.spaces, ' ').trim();
            }
            let guard = state?.parse?.guard?.test(trim)
                ?? TempoClass?.[sym.$guard]?.test(trim)
                ?? true;
            if (!guard) {
                const snip = Object.assign({}, Snippet, state.parse?.snippet);
                const sep = snip[Token.sep]?.source ?? Match.separator.source;
                const mm = snip[Token.mm]?.source ?? Snippet[Token.mm].source;
                const dd = (snip[Token.dd]?.source ?? Snippet[Token.dd].source)
                    .replace('{ord}', snip[Token.ord]?.source ?? Snippet[Token.ord].source);
                const datePrefix = new RegExp(`^\\d{4}${sep}(?:${mm})${sep}(?:${dd})(?:\\b|[T\\s]|$)`, 'i');
                if (datePrefix.test(trim) && (!trim.includes('[') && !trim.includes(']') || Match.bracket.test(trim)))
                    guard = true;
            }
            // 🛡️ Bypass the strict global guard if the current instance is using localized parsing
            if (!guard && (!isEmpty(state.parse.monthMap) || !isEmpty(state.parse.weekdayMap)))
                guard = true;
            // Also bypass if the input itself matches a registered modifier word (e.g. localized 'prochain', 'siguiente')
            if (!guard && state.config.registry?.modifiers) {
                const modWords = Object.values(state.config.registry.modifiers).flat();
                if (modWords.some(w => trim.toLowerCase().includes(w.toLowerCase())))
                    guard = true;
            }
            if (!guard) {
                const keys = (obj) => {
                    const res = new Set();
                    let curr = obj;
                    while (curr && curr !== Object.prototype) {
                        ownKeys(curr).forEach(k => res.add(String(k)));
                        curr = Object.getPrototypeOf(curr);
                    }
                    return res;
                };
                const local = [...keys(state.parse.event), ...keys(state.parse.period)];
                const lowTrim = trim.toLowerCase();
                const bypass = local.some(key => lowTrim.includes(String(key).toLowerCase()));
                if (!bypass)
                    return arg;
            }
            value = trim; // Update value for downstream parsing
        }
        const res = _ParseEngine.parseLayout(state, value, dateTime, isAnchored, resolvingKeys);
        return res;
    },
    /** match a string or number against known layouts */
    parseLayout(state, value, dateTime, isAnchored = false, resolvingKeys = new Set()) {
        const arg = asType(value);
        const { type } = arg;
        const trim = value?.toString().trim() ?? '';
        const resolving = new Set(resolvingKeys);
        const TempoClass = getRuntime().modules['Tempo'];
        if (resolving.size >= 100) {
            logError(new RangeError(`Infinite recursion detected in layout resolution for: ${String(value)}`), state.config);
            return arg;
        }
        if (type === 'String' && isEmpty(trim)) {
            accumulateResult(state, { type: 'Empty', value: trim, match: 'Empty' });
            return Object.assign(arg, { type: 'Empty' });
        }
        let isEpoch = false;
        let finalValue = value;
        let finalType = type;
        const isExplicitUnit = state.userProvidedKeys?.has('timeStamp');
        const isLong = trim.length >= 12 ||
            (state.config.timeStamp === 'ss' && trim.length >= 9) ||
            (isExplicitUnit && trim.length >= 9);
        if (type === 'String' && isNumeric(trim)) {
            const num = Number(trim);
            const isBigInt = Number.isInteger(num) && isLong;
            // ⚡ Only short-circuit as Epoch if it's a fractional number or a long integer.
            // Short integers (like '+6') should fall through to layout matching (e.g. 'offset').
            if (!Number.isInteger(num) || isBigInt) {
                isEpoch = true;
                finalValue = isBigInt ? BigInt(trim) : num;
                finalType = isBigInt ? 'BigInt' : 'Number';
            }
        }
        else if (type === 'BigInt') {
            isEpoch = true;
        }
        else if (type === 'Number') {
            if (!isNumber(value))
                return arg;
            if (!Number.isInteger(value) || isLong) {
                isEpoch = true;
                finalValue = Number.isInteger(value) ? BigInt(value) : value;
                finalType = Number.isInteger(value) ? 'BigInt' : 'Number';
            }
            else if (trim.length <= 7) {
                const msg = 'Cannot safely interpret number with less than 8-digits: use string instead';
                logError(new TypeError(msg), state.config);
                return arg;
            }
        }
        if (isEpoch) {
            const match = { type: finalType, value: finalValue, match: 'Epoch' };
            accumulateResult(state, match);
            return Object.assign(arg, match);
        }
        if (!isZonedDateTime(dateTime))
            return arg;
        let zdt = dateTime;
        const anchorTime = zdt.toPlainTime();
        const orderedPatterns = selectLayoutPatterns(state, trim, {
            enablePrefilter: state.parse.preFilter === true,
            onPlan: (summary) => {
                if (state.parse.preFilter !== true || !state.config?.debug)
                    return;
                const reduced = summary.totalCandidates - summary.selectedCandidates;
                if (reduced <= 0 && !summary.fallbackToFull)
                    return;
                logDebug(`Planner summary: selected ${summary.selectedCandidates}/${summary.totalCandidates}`, state.config, `rules=${summary.rulesApplied.join(',') || 'none'}`, `fallback=${summary.fallbackToFull}`, `input="${summary.inputClass.trim}"`);
            }
        });
        if (state.config?.debug === LOG.Debug)
            logDebug(`[ParseEngine] Selected layouts: ${orderedPatterns.map(p => p[0].description).join(', ')}`, state.config);
        for (const [symKey, pat] of orderedPatterns) {
            const groups = _ParseEngine.parseMatch(state, pat, trim);
            if (isEmpty(groups))
                continue;
            if (state.config?.debug === LOG.Debug)
                logDebug(`[ParseEngine] Matched layout '${symKey.description}' with groups: ${JSON.stringify(groups)}`, state.config);
            const hasTime = Object.keys(groups)
                .some(key => ['hh', 'mi', 'ss', 'ms', 'us', 'ns', 'ff', 'mer'].includes(key) || Match.period.test(key) || (Match.named.test(key) && key.endsWith('tm'))) || Object.values(groups).includes('now');
            accumulateResult(state, { match: symKey.description, value: trim, groups: { ...groups } });
            dateTime = normalizeMatch(groups, dateTime, {
                state,
                isAnchored,
                resolvingKeys,
                subParse: (v, dt, rk) => _ParseEngine.parseLayout(state, v, dt, true, rk),
                conform: (v, dt, rk) => _ParseEngine.conform(state, v, dt, true, rk)
            });
            const isChanged = !dateTime.toPlainTime().equals(anchorTime);
            if (!isAnchored && !hasTime && !isChanged)
                dateTime = dateTime.withPlainTime('00:00:00');
            if (isZonedDateTime(dateTime))
                Object.assign(arg, { type: 'Temporal.ZonedDateTime', value: dateTime, match: symKey.description, groups });
            break;
        }
        return arg;
    },
    /** apply a regex-match against a value, and clean the result */
    parseMatch(state, pat, value) {
        const groups = value.toString().match(pat)?.groups || {};
        ownEntries(groups)
            .forEach(([key, val]) => isEmpty(val) && delete groups[key]);
        return groups;
    },
    /** check if we've been given a ZonedDateTimeLike object */
    isZonedDateTimeLike(state, tempo) {
        if (!isPlainObject(tempo) || isEmpty(tempo))
            return false;
        const keys = ownKeys(tempo);
        if (keys.some(key => state.CONFIG.has(key) && !state.ZONED_DATE_TIME.has(key) && key !== 'value'))
            return false;
        return keys
            .filter(isString)
            .some((key) => state.ZONED_DATE_TIME.has(key) && !state.CONFIG.has(key));
    },
};
const withState = (fn) => {
    return function (...args) {
        const firstArg = args[0];
        let state;
        let callArgs;
        if (isObject(firstArg) && isObject(firstArg.config) && isObject(firstArg.parse)) {
            state = firstArg;
            callArgs = args.slice(1);
        }
        else if (isObject(firstArg) && firstArg.constructor && typeof firstArg.constructor[sym.$Internal] === 'function') {
            state = firstArg.constructor[sym.$Internal]();
            callArgs = args.slice(1);
        }
        else {
            state = this?.[sym.$Internal]?.() ?? getRuntime().state;
            callArgs = args;
        }
        const res = fn(state, ...callArgs);
        return (hasOwn(res, 'type') && hasOwn(res, 'value')) ? res.value : res;
    };
};
/**
 * Public Parse Engine (wrapped for dual-mode support)
 */
export const ParseEngine = {
    /** Parses a DateTime input into a normalized temporal object */
    parse: withState(_ParseEngine.parse),
    /** Conforms a parse result to a specific temporal type */
    conform: withState(_ParseEngine.conform),
    /** Parses input using a specific layout pattern */
    parseLayout: withState(_ParseEngine.parseLayout),
    /** Matches input against the supplied regular expression pattern */
    parseMatch: withState(_ParseEngine.parseMatch),
    /** Checks if input is a ZonedDateTime-like object */
    isZonedDateTimeLike: withState(_ParseEngine.isZonedDateTimeLike),
    /** Accumulates and returns parse results */
    result: withState(accumulateResult)
};
/**
 * # ParseModule
 * The internal parsing engine for Tempo.
 * Decouples date-string interpretation from the core class.
 * @property {string} name - Module identifier
 * @property {string} version - Module version
 * @property {Function} install - Module installation function
 */
export const ParseModule = defineInterpreterModule('ParseModule', ParseEngine);
/**
 * Parses a date-time value using an isolated parser configuration.
 *
 * Standalone parsing defaults to strict mode and applies the supplied configuration overrides.
 *
 * @param value - The date-time value to parse.
 * @param options - Configuration overrides for this parse operation.
 * @returns The parsed date-time as a `Temporal.ZonedDateTime`.
 */
export function parse(value, options = {}) {
    const runtime = getRuntime();
    const globalState = runtime.state ?? init();
    // Create a local state isolated from the global state
    const state = {
        ...globalState,
        config: markConfig({ ...globalState.config }),
        parse: markConfig({ ...globalState.parse })
    };
    // Deep-clone nested mutable objects to prevent global leakage
    state.parse.snippet = { ...globalState.parse.snippet };
    state.parse.layout = { ...globalState.parse.layout };
    state.parse.event = { ...globalState.parse.event };
    state.parse.period = { ...globalState.parse.period };
    state.parse.ignore = { ...globalState.parse.ignore };
    state.parse.pattern = new Map(globalState.parse.pattern);
    // Standalone parsing defaults to 'strict' mode
    const localOptions = { ...options };
    localOptions.mode ??= 'strict';
    state.options = localOptions;
    // Apply options
    extendState(state, localOptions);
    // Register event/period aliases for standalone state before compiling patterns
    const TempoClass = runtime.modules['Tempo'];
    if (TempoClass) {
        TempoClass[$setEvents](state, undefined, false);
        TempoClass[$setPeriods](state, undefined, false);
    }
    // Compile RegEx patterns
    setPatterns(state);
    // Execute the parse
    return ParseEngine.parse(state, value);
}
